import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { san, loadLogoDataUrl, drawLRHeader, resolveBrandName, lrLabel, BASE_STYLES, HEAD_RED, RED } from "./common";

const MARG = { left: 8, right: 8 };

// rupees / paise split for the Rs. | P. columns
function rp(amt) {
  const v = Number(amt || 0);
  if (!v) return ["-", ""];
  const r = Math.floor(v);
  return [r.toLocaleString("en-IN"), String(Math.round((v - r) * 100)).padStart(2, "0")];
}

// Pull "City, State, PIN" out of a full address (drops a trailing "India")
function cityStatePin(s) {
  s = san(s || "").replace(/,?\s*india\.?\s*$/i, "").trim();
  if (!s) return "-";
  const parts = s.split(",").map((p) => p.trim()).filter(Boolean);
  const pin = (s.match(/\b\d{6}\b/) || [])[0];
  if (parts.length <= 1) return parts[0] || s;
  const tail = parts.slice(-2);
  if (pin && !tail.join(" ").includes(pin)) tail.push(pin);
  return tail.join(", ");
}

// small square with a tick (checked) or a cross (unchecked)
function riskBox(doc, x, y, tick) {
  doc.setDrawColor(90); doc.setLineWidth(0.35); doc.rect(x, y - 3, 3.4, 3.4);
  doc.setLineWidth(0.6);
  if (tick) {
    doc.setDrawColor(...RED);
    doc.line(x + 0.6, y - 1.4, x + 1.3, y - 0.4);
    doc.line(x + 1.3, y - 0.4, x + 2.9, y - 2.6);
  } else {
    doc.setDrawColor(120);
    doc.line(x + 0.6, y - 2.6, x + 2.8, y - 0.5);
    doc.line(x + 2.8, y - 2.6, x + 0.6, y - 0.5);
  }
}

export async function buildLRDoc(lr, company) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const logo = await loadLogoDataUrl();
  const copies = ["ORIGINAL — CONSIGNOR COPY", "CONSIGNEE COPY", "DRIVER COPY"];
  for (let i = 0; i < copies.length; i++) {
    if (i > 0) doc.addPage();
    await renderCopy(doc, lr, company, copies[i], logo);
  }
  return doc;
}

export async function generateLRPdf(lr, company) {
  const doc = await buildLRDoc(lr, company);
  doc.save(`${lrLabel(lr.lr_number)}.pdf`);
}

async function renderCopy(doc, lr, company, copyLabel, logo) {
  // professional outer frame (landscape A4 = 297 x 210)
  doc.setDrawColor(...RED); doc.setLineWidth(0.7); doc.rect(6, 6, 285, 198);

  let y = drawLRHeader(doc, company, logo, copyLabel, resolveBrandName(lr, company), lr);

  // Consignor / Consignee — full address
  const _pk = (lr.pickups && lr.pickups.length) ? lr.pickups[0] : null;
  const _dl = (lr.deliveries && lr.deliveries.length) ? lr.deliveries[lr.deliveries.length - 1] : null;
  const cnorName = san((_pk ? _pk.company : "") || lr.consignor_name);
  const cnorAddr = san((_pk ? _pk.address : "") || lr.consignor_address);
  const cnorGst = (_pk ? _pk.gst : "") || lr.consignor_gst;
  const cnorLabel = san(_pk ? _pk.address_label : "");
  const cneeName = san((_dl ? _dl.company : "") || lr.consignee_name);
  const cneeAddr = san((_dl ? _dl.address : "") || lr.consignee_address);
  const cneeGst = (_dl ? _dl.gst : "") || lr.consignee_gst;
  const cneeLabel = san(_dl ? _dl.address_label : "");
  autoTable(doc, {
    startY: y, theme: "grid", margin: MARG, styles: { ...BASE_STYLES, fontSize: 8.5, cellPadding: 1.8 }, headStyles: { ...HEAD_RED, halign: "left" },
    bodyStyles: { minCellHeight: 15, valign: "top" },
    head: [["Consignor (full address)", "Consignee (full address)"]],
    body: [[
      `${cnorName || "-"}\n${cnorLabel ? `[${cnorLabel}]\n` : ""}${cnorAddr || ""}\nGSTIN: ${cnorGst || "-"}`,
      `${cneeName || "-"}\n${cneeLabel ? `[${cneeLabel}]\n` : ""}${cneeAddr || ""}\nGSTIN: ${cneeGst || "-"}`,
    ]],
    columnStyles: { 0: { cellWidth: 140.5 }, 1: { cellWidth: 140.5 } },
  });
  y = doc.lastAutoTable.finalY + 2;

  // Carriage — From/To = City, State, PIN only; Vehicle No only (no driver)
  const vlist = (lr.vehicles && lr.vehicles.length > 0)
    ? lr.vehicles
    : (lr.vehicle_number ? [{ vehicle_number: lr.vehicle_number }] : []);
  const vehCell = vlist.length ? vlist.map((v) => san(v.vehicle_number || "-")).filter(Boolean).join(", ") : "-";
  const mode = lr.transport_mode === "Air" ? "AIR" : ("ROAD" + (lr.road_load_type ? ` ${lr.road_load_type}` : ""));
  autoTable(doc, {
    startY: y, theme: "grid", margin: MARG, styles: { ...BASE_STYLES, halign: "center", cellPadding: 1.6 }, headStyles: HEAD_RED,
    head: [["From (City, State, PIN)", "To (City, State, PIN)", "By", "Delivery", "Vehicle No."]],
    body: [[cityStatePin(cnorAddr || lr.from_location), cityStatePin(cneeAddr || lr.to_location), mode, "DOOR", vehCell]],
    columnStyles: {
      0: { cellWidth: 85, halign: "left" }, 1: { cellWidth: 85, halign: "left" },
      2: { cellWidth: 22 }, 3: { cellWidth: 24 }, 4: { cellWidth: 65, fontStyle: "bold" },
    },
  });
  y = doc.lastAutoTable.finalY + 2;

  // Goods — Nature of Goods = "As per Document No. <doc list>" + E-Way Bill line; weight printed as FIXED; value = O/R
  const fmtD = (d) => { if (!d) return ""; const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d)); return m ? `${m[3]}/${m[2]}/${m[1]}` : san(d); };
  const docList = (lr.documents || []).filter((d) => d && (d.number || "").trim());
  const ewbList = (lr.ewaybills || []).filter((e) => e && (e.number || "").trim());
  const docStr = docList.length
    ? docList.map((d) => `${san(d.number)}${d.date ? ` dt ${fmtD(d.date)}` : ""}`).join(", ")
    : san(lr.client_ref_no || lr.lr_number || "-");
  const ewbStr = ewbList.map((e) => `${san(e.number)}${e.date ? ` dt ${fmtD(e.date)}` : ""}`).join(", ");
  const natureCell = `As per Document No. ${docStr}` + (ewbStr ? `\nE-Way Bill: ${ewbStr}` : "");
  const pkgCount = lr.no_of_packages || (lr.items || []).reduce((s, it) => s + Number(it.quantity || 0), 0) || "";
  autoTable(doc, {
    startY: y, theme: "grid", margin: MARG, styles: { ...BASE_STYLES, fontSize: 8.5, halign: "center", cellPadding: 1.6 }, headStyles: HEAD_RED,
    bodyStyles: { minCellHeight: 11, valign: "middle" },
    head: [["No. of\nPackages", "Method of\nPacking", "Nature of Goods", "Actual\nWeight (kg)", "Value of\nGoods (Rs.)", "Private Marks"]],
    body: [[
      String(pkgCount),
      san((lr.items && lr.items[0] && (lr.items[0].unit || lr.items[0].packing)) || ""),
      natureCell,
      "FIXED",
      "O/R", "",
    ]],
    columnStyles: {
      0: { cellWidth: 30 }, 1: { cellWidth: 40 }, 2: { cellWidth: 120, halign: "left", fontStyle: "bold" },
      3: { cellWidth: 30 }, 4: { cellWidth: 31 }, 5: { cellWidth: 30 },
    },
  });
  y = doc.lastAutoTable.finalY + 2;

  // Custom fields (e.g. LSO Number)
  const cf = (lr.custom_fields || []).filter((f) => f && (f.title || f.value));
  if (cf.length > 0) {
    const rows = [];
    for (let i = 0; i < cf.length; i += 2) {
      const a = cf[i], b = cf[i + 1];
      rows.push([san(a.title || ""), san(a.value || ""), b ? san(b.title || "") : "", b ? san(b.value || "") : ""]);
    }
    autoTable(doc, {
      startY: y, theme: "grid", margin: MARG, styles: { ...BASE_STYLES, fontSize: 8.5 }, headStyles: { ...HEAD_RED, halign: "left" },
      head: [["Detail", "Value", "Detail", "Value"]], body: rows,
      columnStyles: { 0: { cellWidth: 60, fontStyle: "bold" }, 1: { cellWidth: 80.5 }, 2: { cellWidth: 60, fontStyle: "bold" }, 3: { cellWidth: 80.5 } },
    });
    y = doc.lastAutoTable.finalY + 2;
  }

  // Charges
  const [fr, fp] = rp(lr.freight_amount);
  autoTable(doc, {
    startY: y, theme: "grid", margin: MARG, styles: { ...BASE_STYLES, cellPadding: 1.9 }, headStyles: { ...HEAD_RED, halign: "left" },
    bodyStyles: { minCellHeight: 6 },
    head: [["Charges", "Basis", "Rs.", "P."]],
    body: [
      ["Freight", lr.payment_mode || "-", fr, fp],
      ["GTA Service — GST payable by Consignor / Consignee under RCM", "Reverse Charge", "-", ""],
      [{ content: "TOTAL", styles: { fontStyle: "bold" } }, "", { content: fr, styles: { fontStyle: "bold", halign: "right" } }, { content: fp, styles: { fontStyle: "bold" } }],
    ],
    columnStyles: { 0: { cellWidth: 180 }, 1: { cellWidth: 50, halign: "center" }, 2: { cellWidth: 38, halign: "right" }, 3: { cellWidth: 13, halign: "right" } },
  });
  y = doc.lastAutoTable.finalY;   // bottom border of the Charges table

  // ---- Closing block flows from just under the Charges table; the signature boxes
  // and footer stay pinned to the frame bottom. Gaps compress gracefully if the
  // tables above ran long, so nothing ever overlaps the table or the boxes. ----
  const boxY = 178, boxH = 20;          // signature boxes 178 -> 198
  const footerY = 202;                  // footer inside frame bottom (204)

  const declLines = doc.splitTextToSize(
    "The consignor confirms that the above particulars are true and correct, that no prohibited or dangerous goods are included, and agrees to the company's conditions of carriage. Goods are booked entirely at owner's risk and are not insured by the carrier.",
    250);
  const declLH = 3.6;
  // Ideal gaps, scaled down if there isn't quite enough room before the boxes.
  const declSpan = (declLines.length - 1) * declLH;
  const need = 6 + 6.5 + 6.5 + declSpan + 5.5;     // table->remarks->risk->decl->jur
  const have = (boxY - 4) - y;
  const k = Math.max(0.55, Math.min(1, have / need));
  const remarksY = y + 6 * k;
  const riskY = remarksY + 6.5 * k;
  const declTopY = riskY + 6.5 * k;
  const jurY = declTopY + declSpan + 5.5 * k;

  // Remarks — single line
  const remark = san(lr.remarks || "Not availing input tax credit under CGST/SGST/IGST Act, 2017.");
  doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(...RED);
  doc.text("Remarks:", 8, remarksY);
  doc.setFont("helvetica", "normal"); doc.setTextColor(70);
  doc.text(doc.splitTextToSize(remark, 255)[0] || "", 27, remarksY);

  // Risk / handling line with tick + cross
  doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(0);
  doc.text("Goods carried at:", 8, riskY);
  riskBox(doc, 39, riskY, true); doc.text("Owner's Risk", 44, riskY);
  riskBox(doc, 74, riskY, false); doc.setTextColor(120); doc.text("Carrier's Risk", 79, riskY);
  doc.setTextColor(70); doc.setFont("helvetica", "normal"); doc.setFontSize(7.5);
  doc.text("Unloading by consignee   |   Not responsible for breakage, leakage or damage in transit.", 118, riskY);

  // Declaration
  doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(0);
  doc.text("Declaration:", 8, declTopY);
  doc.setFont("helvetica", "normal"); doc.setTextColor(70);
  doc.text(declLines, 30, declTopY, { lineHeightFactor: 1.3 });

  // Jurisdiction — centred, just above the signature boxes
  doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(...RED);
  doc.text("Subject to Mumbai Jurisdiction only.", 148.5, jurY, { align: "center" });

  // Signature + stamp boxes for all three parties
  doc.setDrawColor(150); doc.setLineWidth(0.4);
  doc.rect(8, boxY, 90, boxH);
  doc.rect(103, boxY, 90, boxH);
  doc.rect(198, boxY, 93, boxH);
  doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(...RED);
  doc.text("CONSIGNOR", 11, boxY + 4);
  doc.text("DRIVER", 106, boxY + 4);
  doc.text("FOR " + resolveBrandName(lr, company).toUpperCase(), 201, boxY + 4, { maxWidth: 88 });
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(140);
  doc.text("Signature & Stamp", 11, boxY + boxH - 2);
  doc.text("Signature", 106, boxY + boxH - 2);
  doc.text("Authorised Signatory & Stamp", 201, boxY + boxH - 2);
  doc.setFontSize(6.5); doc.setTextColor(130);
  doc.text(`Computer-generated document  ·  ${copyLabel}  ·  ${resolveBrandName(lr, company)}`, 148.5, footerY, { align: "center" });
}
